/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, toConsumableArray as _toConsumableArray, objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useReducer, useRef, useCallback, Fragment } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import { Checkmark } from '@carbon/react/icons';
import { Checkbox, Tag, DropdownSkeleton } from '@carbon/react';
import debounce from '../../../Utilities/debounce/debounce.js';
import { documentLanguage } from '../../../Utilities/locale/getLocale.js';
import ListBox from './internal/ListBox/ListBox.js';
import ListBoxField from './internal/ListBox/ListBoxField.js';
import ListBoxMenu from './internal/ListBox/ListBoxMenu.js';
import ListBoxMenuItem from './internal/ListBox/ListBoxMenuItem.js';
import ListBoxSelection from './internal/ListBox/ListBoxSelection.js';
import isEqual from './tools/isEqual.js';
import DropdownIcon from './components/DropdownIcon.js';
import ExpandIcon from './components/ExpandIcon.js';
import GroupLabel from './components/GroupLabel.js';
import Selection from './components/Selection.js';
import { defaultItemToString } from './tools/itemToString.js';
import { groupedByCategory } from './tools/groupedByCategory.js';
import { buildHierarchy as _buildHierarchy, defaultCompareItems, defaultSortItems, findParent } from './tools/sorting.js';
import { getAllChildren as _getAllChildren, defaultFilterItems } from './tools/filter.js';
import { useTranslation } from '../../../Utilities/i18n/i18n.js';

var _excluded = ["aria-expanded"];
var find = function find() {
  var items = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var target = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var found;
  items.some(function (item) {
    if (item.id === target.id) {
      found = item;
      return true;
    }
    return false;
  });
  return found;
};
var computeId = function computeId(_ref) {
  var item = _ref.item,
    _ref$itemToString = _ref.itemToString,
    itemToString = _ref$itemToString === void 0 ? defaultItemToString : _ref$itemToString,
    parentId = _ref.parentId;
  return "".concat(parentId ? "".concat(parentId, "-") : '').concat(item.id || itemToString(item));
};
var _flatten = function flatten(_ref2) {
  var category = _ref2.category,
    _ref2$items = _ref2.items,
    items = _ref2$items === void 0 ? [] : _ref2$items,
    level = _ref2.level,
    parentId = _ref2.parentId,
    _ref2$itemToString = _ref2.itemToString,
    itemToString = _ref2$itemToString === void 0 ? defaultItemToString : _ref2$itemToString;
  return items.reduce(function (list, item) {
    var mappedItem = _objectSpread2(_objectSpread2({}, item), {}, {
      id: computeId({
        item: item,
        itemToString: itemToString,
        parentId: parentId
      }),
      category: category || item.category,
      level: level || 0,
      hasChildren: !!item.options,
      parentId: parentId
    });
    list.push(mappedItem);
    if (Array.isArray(item.options) && item.options.length > 0) {
      list.push.apply(list, _toConsumableArray(_flatten({
        category: mappedItem.category,
        items: item.options,
        level: mappedItem.level + 1,
        parentId: mappedItem.id,
        itemToString: itemToString
      })));
    }
    return list;
  }, []);
};
var cleanItem = function cleanItem(item) {
  var result = _objectSpread2({}, item);
  delete result.options;
  delete result.checked;
  return result;
};
var _updateCheckedState = function updateCheckedState(_ref3) {
  var _ref3$options = _ref3.options,
    options = _ref3$options === void 0 ? [] : _ref3$options,
    _ref3$itemToString = _ref3.itemToString,
    itemToString = _ref3$itemToString === void 0 ? defaultItemToString : _ref3$itemToString,
    parentId = _ref3.parentId,
    selectedItems = _ref3.selectedItems;
  return options.map(function (option) {
    var optionId = computeId({
      item: option,
      itemToString: itemToString,
      parentId: parentId
    });
    var result = _objectSpread2({}, option);
    if (result.options) {
      result.options = _updateCheckedState({
        options: result.options,
        itemToString: itemToString,
        parentId: optionId,
        selectedItems: selectedItems
      });
      // The parent is checked only if all its children is checked
      result.checked = !result.options.some(function (opt) {
        return !opt.checked;
      });
    } else {
      result.checked = selectedItems.some(function (selectedItem) {
        return selectedItem.id === optionId;
      });
    }
    return result;
  });
};
var NestedFilterableDropdown = function NestedFilterableDropdown(_ref4) {
  var filterItems = _ref4.filterItems,
    itemToString = _ref4.itemToString,
    mode = _ref4.mode,
    items = _ref4.items,
    onChange = _ref4.onChange,
    canClear = _ref4.canClear,
    containerClassName = _ref4.className,
    disabled = _ref4.disabled,
    hideLabel = _ref4.hideLabel,
    id = _ref4.id,
    locale = _ref4.locale,
    placeholder = _ref4.placeholder,
    ariaLabel = _ref4.ariaLabel,
    sortItems = _ref4.sortItems,
    compareItems = _ref4.compareItems,
    customCategorySorting = _ref4.customCategorySorting,
    showTag = _ref4.showTag,
    showTooltip = _ref4.showTooltip,
    titleText = _ref4.titleText,
    overrideTranslations = _ref4.overrideTranslations,
    initialSelectedItems = _ref4.initialSelectedItems;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    expandedItems = _useState2[0],
    setExpandedItems = _useState2[1];
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    flattenedItems = _useState4[0],
    setFlattenedItems = _useState4[1];
  var _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    inputValue = _useState6[0],
    setInputValue = _useState6[1];
  var _useState7 = useState(),
    _useState8 = _slicedToArray(_useState7, 2),
    typing = _useState8[0],
    setTyping = _useState8[1];
  var _useState9 = useState(null),
    _useState10 = _slicedToArray(_useState9, 2),
    highlightedIndex = _useState10[0],
    setHighLightedIndex = _useState10[1];
  var _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    isOpen = _useState12[0],
    setIsOpen = _useState12[1];
  var _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    isInputFocus = _useState14[0],
    setIsInputFocus = _useState14[1];
  var _useState15 = useState([]),
    _useState16 = _slicedToArray(_useState15, 2),
    flattenedSelectedItems = _useState16[0],
    setFlattenedSelectedItems = _useState16[1];
  var _useState17 = useState([]),
    _useState18 = _slicedToArray(_useState17, 2),
    initialSelectedItemsCache = _useState18[0],
    setInitialSelectedItemsCache = _useState18[1];
  var _useReducer = useReducer(function (x) {
      return x + 1;
    }, 0),
    _useReducer2 = _slicedToArray(_useReducer, 2),
    forceUpdate = _useReducer2[1];
  var inputNode = useRef();
  var handleOnInputValueChange = debounce(function (value, _ref5) {
    var type = _ref5.type;
    if (type === Downshift.stateChangeTypes.changeInput) {
      setInputValue(function (prevInputValue) {
        var newInputValue = Array.isArray(value) ? prevInputValue : value;
        var itemsToExpand = items.reduce(function (toExpand, item) {
          var allChildren = _getAllChildren(item, items);
          if (allChildren.length > 0) {
            var filteredChildren = filterItems(allChildren, {
              itemToString: itemToString,
              newInputValue: newInputValue
            });
            if (filteredChildren.length > 0) {
              if (!newInputValue || find(expandedItems, item)) {
                return toExpand;
              }
              if (!find(toExpand, item)) {
                toExpand.push(item);
              }
            }
          }
          return toExpand;
        }, []);
        setExpandedItems([].concat(_toConsumableArray(expandedItems), _toConsumableArray(itemsToExpand)));
        if (mode !== 'multi') {
          setTyping(true);
        }
        return newInputValue || '';
      });
    }
  }, 20);
  var localFlattenedItems = flattenedItems;
  var localFlattenedSelectedItems = flattenedSelectedItems;
  var localInitialSelectedItemsCache = initialSelectedItemsCache;
  var itemsToProcess = initialSelectedItems ? items.map(function (obj) {
    return initialSelectedItems.find(function (o) {
      return o.id === obj.id;
    }) || obj;
  }) : items;
  var isHierarchical = items.some(function (item) {
    return !!item.options;
  });
  var updatedItems = isHierarchical ? _flatten({
    items: itemsToProcess,
    itemToString: itemToString
  }).map(cleanItem) : itemsToProcess.map(function (item) {
    var copy = item;
    var localId = item.id,
      hasChildren = item.hasChildren,
      level = item.level,
      parentId = item.parentId;
    if (parentId && typeof level === 'undefined') {
      copy.level = _buildHierarchy(item, itemsToProcess).length - 1;
    }
    if (typeof hasChildren === 'undefined') {
      var hasChildrenFlag = itemsToProcess.some(function (i) {
        return i.parentId === localId;
      });
      copy.hasChildren = hasChildrenFlag;
    }
    return copy;
  });
  if (!isEqual(initialSelectedItems, localInitialSelectedItemsCache) || !isEqual(updatedItems, localFlattenedItems)) {
    var updatedSelectedItems = isHierarchical ? _flatten({
      items: initialSelectedItems,
      itemToString: itemToString
    }).filter(function (item, index, itemArray) {
      if (!item.parentId || item.checked) {
        return true;
      }

      // Any parent checked will make all its children checked
      var hierarchy = _buildHierarchy(item, itemArray);
      var parentChecked = hierarchy.some(function (parent) {
        return parent.checked;
      });
      if (parentChecked) {
        return true;
      }

      // Any child checked will make its parent checked
      var allChildren = _getAllChildren(item, itemArray);
      var childChecked = allChildren.some(function (child) {
        return child.checked;
      });
      if (childChecked) {
        return true;
      }

      // If none of the children has the `checked` flag,
      // all children are considered checked.
      var rootAllChildren = _getAllChildren(hierarchy[0], itemArray);
      return rootAllChildren.length > 0 && !rootAllChildren.some(function (child) {
        return child.checked;
      });
    }).map(cleanItem) : initialSelectedItems.reduce(function (list, item) {
      // Any parent checked will make all its children checked
      var hierarchy = _buildHierarchy(item, updatedItems);
      hierarchy.forEach(function (parent) {
        if (!find(list, parent)) {
          list.push(_objectSpread2({}, parent));
        }
      });

      // If none of the children has the `checked` flag,
      // all children are considered checked.
      var allChildren = _getAllChildren(item, updatedItems);
      if (!allChildren.some(function (child) {
        return find(list, child);
      })) {
        list.push.apply(list, _toConsumableArray(allChildren.map(function (child) {
          return _objectSpread2({}, child);
        })));
      }
      return list;
    }, _toConsumableArray(initialSelectedItems));
    localFlattenedItems.splice.apply(localFlattenedItems, [0, localFlattenedItems.length].concat(_toConsumableArray(updatedItems)));
    localFlattenedSelectedItems.splice.apply(localFlattenedSelectedItems, [0, localFlattenedSelectedItems.length].concat(_toConsumableArray(updatedSelectedItems)));
    if (mode === 'single') {
      localFlattenedSelectedItems.splice(1);

      // Never allow a parent to be selected in single mode
      var selectedItemLabel = '';
      if (localFlattenedSelectedItems[0] && _getAllChildren(localFlattenedSelectedItems[0], localFlattenedItems).length === 0) {
        selectedItemLabel = itemToString(localFlattenedSelectedItems[0]);
      } else {
        localFlattenedSelectedItems.splice(0);
      }
      setFlattenedItems(localFlattenedItems);
      setFlattenedSelectedItems(localFlattenedSelectedItems);
      setInitialSelectedItemsCache(initialSelectedItems);
      setInputValue(selectedItemLabel || '');
    }
    setFlattenedItems(localFlattenedItems);
    setFlattenedSelectedItems(localFlattenedSelectedItems);
    setInitialSelectedItemsCache(initialSelectedItems);
  }
  var onChangeDeps = useRef();
  onChangeDeps.current = {
    itemToString: itemToString,
    items: items,
    mode: mode,
    onChange: onChange
  };
  var handleOnChange = useCallback(function (_ref6) {
    var _ref6$selectedItems = _ref6.selectedItems,
      selectedItems = _ref6$selectedItems === void 0 ? [] : _ref6$selectedItems;
    var isMulti = onChangeDeps.current.mode === 'multi';
    var localIsHierarchical = onChangeDeps.current.items.some(function (item) {
      return !!item.options;
    });
    var mappedSelectedItems = isMulti && localIsHierarchical ? onChangeDeps.current.items.reduce(function (list, item) {
      if (find(selectedItems, item)) {
        var selectedItem = _objectSpread2({}, item);
        if (item.options) {
          selectedItem.options = _updateCheckedState({
            options: item.options,
            itemToString: onChangeDeps.current.itemToString,
            parentId: computeId({
              item: item,
              itemToString: onChangeDeps.current.itemToString
            }),
            selectedItems: selectedItems
          });
        }
        list.push(selectedItem);
      }
      return list;
    }, []) : selectedItems;
    setFlattenedSelectedItems(selectedItems);
    if (!isMulti) {
      var _selectedItemLabel = selectedItems[0] && onChangeDeps.current.itemToString(selectedItems[0]);
      setIsOpen(false);
      setInputValue(_selectedItemLabel || '');
      setTyping(false);
    }
    if (onChangeDeps.current.onChange) {
      onChangeDeps.current.onChange({
        selectedItems: mappedSelectedItems
      });
    }
  }, [onChangeDeps]);
  var onToggle = function onToggle(item) {
    var isExpanded = find(expandedItems, item);
    if (!isExpanded) {
      setExpandedItems(function (oldExpandedItems) {
        return [].concat(_toConsumableArray(oldExpandedItems), [item]);
      });
    } else {
      setExpandedItems(function (oldExpandedItems) {
        return oldExpandedItems.filter(function (expandedItem) {
          return expandedItem.id !== item.id;
        });
      });
    }
  };
  var handleOnOuterClick = function handleOnOuterClick() {
    setIsInputFocus(false);
    setIsOpen(false);
    if (mode === 'multi') {
      setInputValue('');
    } else {
      var _selectedItemLabel2 = flattenedSelectedItems[0] && itemToString(flattenedSelectedItems[0]);
      setInputValue(_selectedItemLabel2 || '');
      setTyping(false);
    }
  };
  var handleOnStateChange = function handleOnStateChange(_ref7) {
    var type = _ref7.type,
      newInputValue = _ref7.inputValue,
      newHighlightedIndex = _ref7.highlightedIndex,
      newIsOpen = _ref7.isOpen;
    switch (type) {
      case Downshift.stateChangeTypes.changeInput:
        setInputValue(newInputValue);
        setIsOpen(true);
        break;
      case Downshift.stateChangeTypes.keyDownArrowUp:
      case Downshift.stateChangeTypes.itemMouseEnter:
        setHighLightedIndex(newHighlightedIndex || 0);
        break;
      case Downshift.stateChangeTypes.keyDownArrowDown:
        setHighLightedIndex(newHighlightedIndex || 0);
        setIsOpen(true);
        break;
      case Downshift.stateChangeTypes.keyDownEscape:
      case Downshift.stateChangeTypes.mouseUp:
        setIsOpen(false);
        break;
      // Opt-in to some cases where we should be toggling the menu based on
      // a given key press or mouse handler
      // Reference: https://github.com/paypal/downshift/issues/206
      case Downshift.stateChangeTypes.clickButton:
      case Downshift.stateChangeTypes.keyDownSpaceButton:
        setIsOpen(function () {
          var nextIsOpen = newIsOpen || false;
          if (!newIsOpen) {
            // If Downshift is trying to close the menu, but we know the input
            // is the active element in thedocument, then keep the menu open
            if (inputNode === document.activeElement) {
              nextIsOpen = true;
            }
          }
          return nextIsOpen;
        });
        break;
      case Downshift.stateChangeTypes.controlledPropUpdatedSelectedItem:
        // Force to re-render to re-populate the highlighed item
        forceUpdate();
        break;
    }
  };
  var getClearInputValueHandler = function getClearInputValueHandler(clearSelection) {
    return function (event) {
      var _inputNode$focus;
      event.stopPropagation();
      setInputValue('');
      if (mode !== 'multi') {
        setFlattenedSelectedItems([]);
        clearSelection();
      }
      inputNode === null || inputNode === void 0 || (_inputNode$focus = inputNode.focus) === null || _inputNode$focus === void 0 || _inputNode$focus.call(inputNode);
    };
  };
  var getParentItem = function getParentItem(item) {
    return findParent(item, flattenedItems);
  };
  var onItemChange = function onItemChange(item, selectedItems, onChangeFunc) {
    if (item) {
      var toRemove = !!find(selectedItems, item);
      var itemsChanged = [item];
      if (mode === 'multi') {
        if (item.parentId) {
          // Walk parents
          var parents = _buildHierarchy(item, flattenedItems).reverse();
          parents.shift();
          parents.some(function (parent) {
            var isSelected = !!find(selectedItems, parent);
            var children = selectedItems.filter(function (selectedItem) {
              return selectedItem.parentId === parent.id;
            });
            if (children.length === 1 && toRemove && isSelected) {
              // Uncheck parent too and keep walking up
              itemsChanged.push(parent);
              return false;
            }
            if (!toRemove && !isSelected) {
              // Check parent too
              itemsChanged.push(parent);
              return false;
            }
            // If selecting a new item, we need to keep going up to
            // make sure all parents are checked.
            // If unselecting an item, we will break out when the
            // current parent does not need to be removed
            return toRemove;
          });
        }

        // Walk children
        var children = _getAllChildren(item, flattenedItems);
        if (children.length > 0) {
          children.forEach(function (child) {
            var isSelected = !!find(selectedItems, child);
            if (toRemove && isSelected) {
              // Uncheck the child too
              itemsChanged.push(child);
            } else if (!toRemove && !isSelected) {
              // Check the child too
              itemsChanged.push(child);
            }
          });
        }
      }
      onChangeFunc(itemsChanged);
    }
  };
  var className = classnames('cds--multi-select', 'cds--combo-box', 'cpc--nested-filterable-dropdown', containerClassName, {
    'cds--list-box--expanded': isOpen,
    'cpc--nested-filterable-dropdown--focus': isInputFocus
  });
  var titleClasses = classnames('cds--label', {
    'cds--label--disabled': disabled,
    'cds--visually-hidden': hideLabel
  });
  var isMulti = mode === 'multi';
  var canClearSelection = isMulti && isOpen || !isMulti && canClear;
  var currentIndex = -1;
  var highlighted;
  return /*#__PURE__*/React.createElement(Selection, {
    onChange: handleOnChange,
    initialSelectedItems: flattenedSelectedItems,
    mode: mode,
    render: function render(_ref8) {
      var selectedItems = _ref8.selectedItems,
        localOnItemChange = _ref8.onItemChange,
        clearSelection = _ref8.clearSelection;
      return /*#__PURE__*/React.createElement(Downshift, {
        highlightedIndex: highlightedIndex,
        isOpen: isOpen,
        inputValue: inputValue,
        onInputValueChange: handleOnInputValueChange,
        onChange: function onChange(item) {
          onItemChange(item, selectedItems, localOnItemChange);
        },
        itemToString: itemToString,
        onStateChange: handleOnStateChange,
        onOuterClick: handleOnOuterClick,
        selectedItem: selectedItems
      }, function (_ref9) {
        var getToggleButtonProps = _ref9.getToggleButtonProps,
          getInputProps = _ref9.getInputProps,
          getMenuProps = _ref9.getMenuProps,
          getItemProps = _ref9.getItemProps,
          getRootProps = _ref9.getRootProps,
          getLabelProps = _ref9.getLabelProps,
          localIsOpen = _ref9.isOpen,
          localInputValue = _ref9.inputValue,
          selectedItem = _ref9.selectedItem;
        var _getRootProps = getRootProps({
            refKey: 'innerRef',
            'aria-haspopup': undefined,
            'aria-labelledby': undefined,
            'aria-owns': undefined,
            role: undefined
          }, {
            suppressRefError: true
          }),
          ariaExpanded = _getRootProps['aria-expanded'],
          rootProps = _objectWithoutProperties(_getRootProps, _excluded);
        var menuProps = isOpen ? getMenuProps({
          'aria-expanded': ariaExpanded,
          'aria-label': 'Choose an item',
          'aria-labelledby': undefined
        }) : undefined;
        return /*#__PURE__*/React.createElement(React.Fragment, null, titleText &&
        /*#__PURE__*/
        // eslint-disable-next-line jsx-a11y/label-has-associated-control
        React.createElement("label", _extends({
          className: titleClasses
          // eslint-disable-next-line react/jsx-props-no-spreading
        }, getLabelProps({
          id: id && "".concat(id, "_label"),
          htmlFor: id
        })), titleText), /*#__PURE__*/React.createElement(ListBox, _extends({
          className: className,
          disabled: disabled
          // eslint-disable-next-line react/jsx-props-no-spreading
        }, rootProps), /*#__PURE__*/React.createElement(ListBoxField, _extends({
          tabIndex: "-1"
          // eslint-disable-next-line react/jsx-props-no-spreading
        }, getToggleButtonProps({
          disabled: disabled,
          role: undefined,
          'aria-label': undefined,
          'aria-labelledby': titleText && id && "".concat(id, "_label")
        })), isMulti && selectedItem.length > 0 && /*#__PURE__*/React.createElement(ListBoxSelection, {
          clearSelection: clearSelection,
          disabled: disabled,
          selectionCount: selectedItem.filter(function (item) {
            return !item.hasChildren;
          }).length,
          overrideTranslations: overrideTranslations
        }), /*#__PURE__*/React.createElement("input", _extends({
          className: "cds--text-input",
          "aria-label": ariaLabel || placeholder,
          ref: inputNode
          // eslint-disable-next-line react/jsx-props-no-spreading
        }, getInputProps({
          disabled: disabled,
          id: id,
          placeholder: placeholder,
          role: 'combobox',
          onBlur: handleOnOuterClick,
          onFocus: function onFocus() {
            setIsInputFocus(true);
          },
          onKeyDown: function onKeyDown(e) {
            e.stopPropagation();
            if (localIsOpen && e.which === 9) {
              // Tab key
              setIsOpen(false);
            }
            var highlightedItem = highlighted && highlighted.item;
            if (highlightedItem) {
              if (e.which === 40) {
                // Down arrow
                if (highlightedItem.hasChildren && !expandedItems.includes(highlightedItem)) {
                  onToggle(highlightedItem);
                }
              } else if (e.which === 38) {
                // Up arrow
                var parentItem = getParentItem(highlightedItem);
                if (parentItem && highlighted.parentIndex > -1 && highlighted.index === highlighted.parentIndex + 1 && expandedItems.includes(parentItem)) {
                  onToggle(parentItem);
                }
              }
            }
          },
          onKeyUp: function onKeyUp(e) {
            var which = e.which;
            if (which === 27) {
              setIsOpen(false);
            }
          },
          'aria-controls': menuProps === null || menuProps === void 0 ? void 0 : menuProps.id,
          'aria-expanded': ariaExpanded,
          'aria-labelledby': titleText && id && "".concat(id, "_label")
        }))), localInputValue && canClearSelection && /*#__PURE__*/React.createElement(ListBoxSelection, {
          clearSelection: getClearInputValueHandler(clearSelection),
          overrideTranslations: overrideTranslations
        }), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(DropdownIcon, {
          isOpen: localIsOpen,
          overrideTranslations: overrideTranslations
        }))), localIsOpen && /*#__PURE__*/React.createElement(ListBoxMenu, _extends({
          style: {
            maxHeight: '424px',
            overflowX: 'hidden',
            paddingTop: '8px'
          }
          // eslint-disable-next-line react/jsx-props-no-spreading
        }, menuProps, {
          ref: menuProps.ref
        }), !filterItems(flattenedItems, {
          itemToString: itemToString,
          inputValue: isMulti || typing ? localInputValue : '',
          expandedItems: expandedItems
        }).length && /*#__PURE__*/React.createElement(ListBoxMenuItem, {
          disabled: true
        }, /*#__PURE__*/React.createElement("div", {
          className: "cds--list-box__menu-item__option cds--list-box__menu-item--single"
        }, t('No matching options'))), groupedByCategory(flattenedItems, customCategorySorting).map(function (group, index) {
          var hasGroups = !!(group[0] !== 'undefined');
          var inputValueToFilter = isMulti || typing ? localInputValue : '';
          var filteredItems = filterItems(group[1], {
            itemToString: itemToString,
            inputValue: inputValueToFilter,
            expandedItems: expandedItems
          });
          var categoryName = '';
          if (hasGroups) {
            categoryName = group[0].toUpperCase();
          }
          var listItems = sortItems(filteredItems, {
            selectedItems: selectedItems,
            itemToString: itemToString,
            compareItems: compareItems,
            locale: locale
          });
          return /*#__PURE__*/React.createElement(Fragment, {
            key: group[0] || index
          }, hasGroups && filteredItems.length > 0 && /*#__PURE__*/React.createElement("div", {
            className: "cds--group-item"
          }, /*#__PURE__*/React.createElement(GroupLabel, {
            key: categoryName
          }, categoryName)), listItems.map(function (item, itemIndex, itemArr) {
            currentIndex += 1;
            if (highlightedIndex === currentIndex) {
              var parentItem = getParentItem(item);
              highlighted = {
                item: item,
                index: itemIndex,
                parentIndex: parentItem ? itemArr.indexOf(parentItem) : -1
              };
            }
            var itemProps = getItemProps({
              item: item,
              index: currentIndex
            });
            var itemText = itemToString(item);
            var tooltipText = item.tag ? "".concat(itemText, " - ").concat(item.tag) : itemText;
            var isChecked = selectedItem.filter(function (selected) {
              return selected.id === item.id;
            }).length > 0;
            var subOptions = _getAllChildren(item, flattenedItems);
            var groupIsOpen = !!find(expandedItems, item);
            var hasGroupsLocal = subOptions.length > 0;
            var itemStyle = item.level ? {
              paddingLeft: "".concat(item.level * 19, "px")
            } : undefined;
            var itemClassName = classnames('cds--list-box__menu-item__option', {
              'cds--list-box__menu-item--single': !isMulti
            }, {
              'cpc--nested-filterable-dropdown--expandable': hasGroupsLocal && !isMulti
            });
            var labelNode;
            if (isMulti) {
              var myCheckedOptions = subOptions.filter(function (subOption) {
                return selectedItem.filter(function (selected) {
                  return selected.id === subOption.id;
                }).length > 0;
              });
              var myUncheckedOptions = subOptions.filter(function (subOption) {
                return selectedItem.filter(function (selected) {
                  return selected.id === subOption.id;
                }).length === 0;
              });
              labelNode = /*#__PURE__*/React.createElement(Checkbox, {
                id: "cb-".concat(itemProps.id),
                name: itemText,
                checked: isChecked,
                indeterminate: myCheckedOptions && myUncheckedOptions && myCheckedOptions.length > 0 && myUncheckedOptions.length > 0,
                readOnly: true,
                tabIndex: "-1",
                title: showTooltip ? tooltipText : undefined,
                labelText: itemText
              });
            } else {
              labelNode = /*#__PURE__*/React.createElement("div", {
                className: "cds--list-box__menu-item__label",
                title: showTooltip ? tooltipText : undefined
              }, itemText);
            }
            return /*#__PURE__*/React.createElement(ListBoxMenuItem, _extends({
              key: "menuItem-".concat(itemIndex) // eslint-disable-line react/no-array-index-key
              ,
              style: itemStyle,
              isActive: isChecked,
              isHighlighted: highlightedIndex === currentIndex,
              tabIndex: isMulti ? '-1' : '0'
              // eslint-disable-next-line react/jsx-props-no-spreading
            }, itemProps, {
              onClick: function onClick(e) {
                var clickOutOfCheckBox = subOptions.length > 0 && e.target.localName !== 'label' && e.target.localName !== 'input';
                if (clickOutOfCheckBox) {
                  onToggle(item);
                } else {
                  onItemChange(item, selectedItems, localOnItemChange);
                }
              }
            }), /*#__PURE__*/React.createElement("div", {
              className: itemClassName
            }, labelNode, item.tag && showTag && /*#__PURE__*/React.createElement(Tag, null, item.tag), hasGroupsLocal && /*#__PURE__*/React.createElement(ExpandIcon, {
              isExpanded: groupIsOpen,
              overrideTranslations: overrideTranslations
            }), !isMulti && !hasGroupsLocal && isChecked && /*#__PURE__*/React.createElement(Checkmark, {
              className: "cds--list-box__menu-item--checkmark"
            })));
          }));
        }))));
      });
    }
  });
};
NestedFilterableDropdown.propTypes = {
  /**
   * Provide a compare function that is used to determine the ordering of
   * options. `compareItems` has the following function signature:
   *
   * compareFunction :
   *  (itemA: string, itemB: string, { locale: string }) => number
   */
  compareItems: PropTypes.func,
  /**
   * Provide a method that sorts all options in the control. Overriding this
   * prop means that you also have to handle the sort logic for selected versus
   * un-selected items. If you just want to control ordering, consider the
   * `compareItems` prop instead.
   *
   * `sortItems` has the following signature:
   *
   * sortItems :
   *   (items: Array<Item>, {
   *     selectedItems: Array<Item>,
   *     itemToString: Item => string,
   *     compareItems: (itemA: string, itemB: string, {
   *       locale: string
   *     }) => number,
   *     locale: string,
   *   }) => Array<Item>
   */
  sortItems: PropTypes.func,
  /**
   * Provide a method that filters the options in the control based on the filter text.
   *
   * `filterItems` has the following signature:
   *
   * filterItems :
   *   (items: Array<Item>, {
   *     itemToString: Item => string,
   *     inputValue: string,
   *     expandedItems: Array<Item>,
   *   }) => Array<Item>
   */
  filterItems: PropTypes.func,
  /**
   * A string for custom class to add to this component.
   */
  className: PropTypes.string,
  /**
   * A boolean to indicate if the current selection can be clear. Only applicable to `single` select mode.
   */
  canClear: PropTypes.bool,
  /**
   * Disable the control
   */
  disabled: PropTypes.bool,
  /**
   * Specify whether the title text should be hidden or not
   */
  hideLabel: PropTypes.bool,
  /**
   * An id to assign to this component.
   */
  id: PropTypes.string,
  /**
   * We try to stay as generic as possible here to allow individuals to pass
   * in a collection of whatever kind of data structure they prefer
   */
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  /**
   * Allow users to pass in arbitrary items from their collection that are
   * pre-selected
   */
  initialSelectedItems: PropTypes.arrayOf(PropTypes.shape({})),
  /**
   * Helper function passed to downshift that allows the library to render a
   * given item to a string label. By default, it extracts the `label` field
   * from a given item to serve as the item label in the list.
   */
  itemToString: PropTypes.func,
  /**
   * Specify the locale of the control. Used for the default `compareItems`
   * used for sorting the list of items in the control.
   */
  locale: PropTypes.string,
  /**
   * `onChange` is a utility for this controlled component to communicate to a
   * consuming component what kind of internal state changes are occuring.
   */
  onChange: PropTypes.func,
  /**
   * Generic `placeholder` that will be used as the textual representation of
   * what this field is for
   */
  placeholder: PropTypes.string.isRequired,
  /**
   * 'aria-label' of the text input field.
   */
  ariaLabel: PropTypes.string,
  /**
   * `single` to allow single select an item in the dropdown.
   * `multi` to allow multiple select items in the dropdown.
   */
  mode: PropTypes.oneOf(['single', 'multi']),
  /**
   * `customCategorySorting` is use to sort the items by category, aphabetic order if not specify
   */
  customCategorySorting: PropTypes.func,
  /**
   * `true` to show tag found in the item.
   */
  showTag: PropTypes.bool,
  /**
   * `true` to show tooltip.
   */
  showTooltip: PropTypes.bool,
  /**
   * Provide the title text that will be read by a screen reader when
   * visiting this control
   */
  titleText: PropTypes.node,
  /**
   * Callback function for overriding translations
   */
  overrideTranslations: PropTypes.func
};
NestedFilterableDropdown.defaultProps = {
  compareItems: defaultCompareItems,
  canClear: true,
  className: undefined,
  hideLabel: undefined,
  id: undefined,
  customCategorySorting: undefined,
  titleText: undefined,
  ariaLabel: undefined,
  onChange: undefined,
  disabled: false,
  filterItems: defaultFilterItems,
  initialSelectedItems: [],
  itemToString: defaultItemToString,
  locale: documentLanguage,
  sortItems: defaultSortItems,
  mode: 'multi',
  showTag: true,
  showTooltip: true,
  overrideTranslations: undefined
};
NestedFilterableDropdown.skeleton = DropdownSkeleton;
var NestedFilterableDropdown$1 = NestedFilterableDropdown;

export { NestedFilterableDropdown$1 as default };
